/**
 * Planning chart indicator configuration.
 *
 * The planning chart reuses the shared IndicatorSidePanel but with a curated set
 * of groups for 30m planning (Per-candle Profiles, Volume, Options / OI, Session
 * Levels) and unified Session options for the session VP/TPO profiles (Full /
 * ETH-RTH / RTH). TPO here is bars only (no Letters / Display pickers). The
 * Option Exposure (GEX) group is intentionally excluded.
 *
 * The Options / OI and Session Levels group definitions are reused verbatim from
 * the shared INDICATOR_GROUPS so they stay in sync with the main chart.
 */

import { INDICATOR_GROUPS } from '../components/IndicatorSidePanel';
import { OVERLAY_TOGGLE_DEFAULTS } from '../indicators/underlyingOverlays';

// Shared Session option set offered by both the session VP and session TPO.
const SESSION_OPTIONS = [
  { value: 'full', label: 'Full' },
  { value: 'eth-rth', label: 'ETH/RTH' },
  { value: 'rth', label: 'RTH' },
];

const baseGroup = (id) => INDICATOR_GROUPS.find((g) => g.id === id);

// Per-candle profiles group (planning-only). Parent toggle drives the 30m
// mini-profiles; the Volume/TPO sub-toggles configure them. Profiles cover the
// full session, so every candle shown on the chart gets one (no session picker).
const profilesGroup = {
  id: 'profiles',
  label: 'Per-candle Profiles',
  color: '#38bdf8',
  description: '30m per-candle VP + TPO mini-profiles',
  indicators: [
    {
      id: 'candleProfiles',
      label: 'Per-candle Profiles',
      description: 'VP + TPO mini-profiles on each 30m candle',
      subToggles: [
        { id: 'candleProfileVolume', label: 'Volume', desc: ['Show per-candle volume profile', 'Per-candle volume profile visible'] },
        { id: 'candleProfileTpo', label: 'TPO', desc: ['Show per-candle TPO bars', 'Per-candle TPO bars visible'] },
      ],
    },
  ],
};

// Volume group: reuse delta/vwap/cvd verbatim, but rebuild the VP and TPO
// entries with a single Session picker each (identical options). Fresh objects
// so no obsolete picker fields survive from the shared definitions.
const baseVolume = baseGroup('volume');
const volumeGroup = {
  ...baseVolume,
  indicators: baseVolume.indicators.map((ind) => {
    if (ind.id === 'volumeProfile') {
      return {
        id: ind.id,
        label: ind.label,
        description: ind.description,
        hasModePicker2: true,
        modePickerId2: 'vpSessionScope',
        modeOptions2: SESSION_OPTIONS,
        hasSubToggle: true, subToggleId: 'nakedPOC',
        subToggleLabels: ['Naked POC', 'Naked POC'],
        subToggleDesc: ['Show untested settled-session POCs', 'Untested POCs visible'],
        subToggles: [
          { id: 'hvnZones', label: 'HVN Zones',
            desc: ['Show high volume node bands (last 5 RTH sessions)', 'High volume node bands visible'] },
        ],
      };
    }
    if (ind.id === 'tpoProfile') {
      return {
        id: ind.id,
        label: ind.label,
        description: 'Market Profile bars with POC and value area',
        hasModePicker2: true,
        modePickerId2: 'tpoSessionScope',
        modeOptions2: SESSION_OPTIONS,
        hasSubToggle: true, subToggleId: 'nakedTpoPOC',
        subToggleLabels: ['Naked TPO POC', 'Naked TPO POC'],
        subToggleDesc: ['Show untested settled-session TPO POCs', 'Untested TPO POCs visible'],
      };
    }
    return ind;
  }),
};

// Curated planning sidebar: profiles + volume + options/OI + session levels.
export const PLANNING_GROUPS = [
  profilesGroup,
  volumeGroup,
  baseGroup('options'),
  baseGroup('compare'),
  baseGroup('levels'),
];

export const PLANNING_INDICATOR_DEFAULTS = {
  // Options / OI context (drives the OI fetches for the Options group)
  expiryMode: '0dte',
  underlying: 'spx',
  oiData: 'settled',
  // Per-candle profiles (on by default, matching the prior planning chart)
  candleProfiles: true,
  candleProfileVolume: true,
  candleProfileTpo: true,
  // Session profiles
  volumeProfile: true,
  nakedPOC: true,
  nakedTpoPOC: false,
  hvnZones: true,
  vpSessionScope: 'full',
  deltaProfile: false,
  tpoProfile: true,
  tpoSessionScope: 'full',
  vwap: false,
  vwapSessionMode: 'full',
  vwapBand1: false,
  vwapBand2: false,
  cvd: false,
  // Session levels (off by default; children of priorLevels default-on)
  priorLevels: false,
  prevDayLevels: true,
  priorWeekLevels: true,
  priorMonthLevels: true,
  overnightLevel: false,
  asiaLevels: false,        // Child of overnightLevel (gated by parent)
  londonLevels: false,      // Child of overnightLevel (gated by parent)
  openingRange: false,
  initialBalance: false,
  openingPrints: false,
  pivotPoints: false,
  sessionMarkers: false,
  // Options / OI (off by default; greek/distribution sub-toggles default-on)
  zeroLevels: false,
  zeroLevelsGex: true,
  zeroLevelsDex: true,
  zeroLevelsVex: true,
  zeroLevelsCex: true,
  gammaLevels: false,
  gammaLevelsGvwap: true,
  gammaLevelsBand: true,
  gammaLevelsWalls: true,
  showExposureProfile: false,
  expProfileGex: true,
  expProfileDex: true,
  expProfileVex: true,
  expProfileCex: true,
  exposureProfileMode: 'dealer',
  showExpProfileHeatmap: false,
  showExpProfileT1: false,
  expProfileAnchorFrac: null,    // drag-to-place open-session profile axis (null = left-edge pin)
  expProfileT1AnchorFrac: null,  // drag-to-place T+1 profile axis (null = right-edge pin)
  hedgeDemandAnchorFrac: null,   // drag-to-place Hedging Demand axis (null = pin right of the exposure axis)
  oiProfileAnchorFrac: null,     // drag-to-place OI distribution axis (null = left-edge pin)
  exposure: false,           // Exposure pane (sub-toggle of showExposureProfile)
  exposureMode: 'dealer',    // inert: the pane follows exposureProfileMode since the 2026-08-10 nesting
  hedgingDemand: false,
  showHedgeDemandHeatmap: false,
  hedgeDemandRealized: false,
  showIvSkew: false,
  ivSkewAnchorFrac: null,       // drag-to-place IV Skew axis (null = offset default pin)
  ivSkewBand: true,             // IV Skew sub-layer (gated by showIvSkew parent)
  ivSkewShape: false,             // Shape History pane (IV Skew sub-toggle, opt-in)
  ivRealized: false,             // IV Index realized-vol companion
  iv: false,
  ivStdDev: false,
  ...OVERLAY_TOGGLE_DEFAULTS,  // indexOverlays parent + overlay children (registry SSOT)
  showForwardOi: false,
  showOiDistribution: false,
  oiDistCallOi: true,
  oiDistPutOi: true,
  oiDistNet: true,
  showOptionsVolume: false,
  optVolCalls: true,
  optVolPuts: true,
  optVolNet: true,
  optVolCumulative: false,       // Child of showOptionsVolume: day-to-date second histogram
  optVolSplit: false,            // Child of showOptionsVolume: at-ask/at-bid paired rows
  optVolFlowNet: false,          // Child of showOptionsVolume: net residual bars (precedence over split)
  optVolFlowPane: false,         // Child of showOptionsVolume: intraday net dealer flow pane
  optVolAnchorFrac: null,        // drag-to-place Options Volume axis (null = offset default pin)
  optVolCumAnchorFrac: null,     // drag-to-place cumulative volume axis (null = offset default pin)
  showUnderlyingCompare: false,      // Underlying Compare strip: right-docked ES/SPX/SPY per-strike lanes
  underlyingCompareMode: 'oi',       // Compare strip fill: 'oi' | 'exposure' | 'volume'
  underlyingCompareGreek: 'gex',     // Compare strip exposure-mode greek
  underlyingCompareZeroLevels: false, // Compare strip: per-lane zero-crossing ticks
  underlyingCompareGammaLevels: false, // Compare strip: per-lane GVWAP + walls
  showProjectedCurves: false,
  projGex: true,
  projDex: true,
  projVex: true,
  projCex: true,
  expectedMove: false,
};
